import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'

import { createTemplate, updateTemplate, submitTemplate } from '../../../actions/template'
import './TemplateForm.css'

const LANGUAGES = [
  { value: 'es_CO', label: 'Español (COL)' },
  { value: 'es_MX', label: 'Español (MEX)' },
  { value: 'es_AR', label: 'Español (ARG)' },
  { value: 'es_ES', label: 'Español (ESP)' },
  { value: 'en_US', label: 'Inglés (US)' },
  { value: 'pt_BR', label: 'Portugués (BR)' }
]

const HEADER_TYPES = [
  { value: '', label: 'Ninguno' },
  { value: 'TEXT', label: 'Texto' },
  { value: 'IMAGE', label: 'Imagen' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Documento' }
]

const BUTTON_CONFIGS = [
  { value: '', label: 'Ninguno' },
  { value: 'QUICK_REPLY', label: 'Respuestas rápidas' },
  { value: 'URL', label: 'Llamada a la acción (URL)' },
  { value: 'PHONE_NUMBER', label: 'Llamada a la acción (Teléfono)' }
]

const EMPTY_LANG_CONTENT = () => ({
  header: null,
  body: { text: '', examples: [] },
  footer: '',
  buttons: []
})

function extractVarCount (text) {
  const matches = text.match(/\{\{\d+\}\}/g) ?? []
  return new Set(matches).size
}

function fillExamples (text, examples) {
  let result = text
  examples.forEach((ex, i) => {
    result = result.replaceAll(`{{${i + 1}}}`, ex || `{{${i + 1}}}`)
  })
  return result
}

function TemplateForm ({ templateToEdit, onSaved, onCancel }) {
  const client = useSelector((state) => state.client)
  const textareaRef = useRef(null)
  const [loading, setLoading] = useState(false)

  const [form, setForm] = useState({
    name: '',
    category: 'MARKETING',
    activeLanguage: '',
    langContent: {}
  })

  // Load templateToEdit into state
  useEffect(() => {
    if (templateToEdit) {
      const code = templateToEdit.language ?? ''
      setForm({
        name: templateToEdit.name ?? '',
        category: templateToEdit.category ?? 'MARKETING',
        activeLanguage: code,
        langContent: {
          [code]: {
            header: templateToEdit.header ?? null,
            body: { text: templateToEdit.body?.text ?? '', examples: templateToEdit.body?.examples ?? [] },
            footer: templateToEdit.footer ?? '',
            buttons: templateToEdit.buttons ?? []
          }
        }
      })
    } else {
      setForm({ name: '', category: 'MARKETING', activeLanguage: '', langContent: {} })
    }
  }, [templateToEdit])

  // Sync examples array size for active language when its body.text changes
  const activeBodyText = form.langContent[form.activeLanguage]?.body?.text ?? ''
  useEffect(() => {
    if (!form.activeLanguage) return
    const count = extractVarCount(activeBodyText)
    setForm(prev => {
      const content = prev.langContent[prev.activeLanguage]
      if (!content) return prev
      const examples = [...(content.body.examples ?? [])]
      const next = examples.slice(0, count)
      while (next.length < count) next.push('')
      if (next.length === examples.length) return prev
      return {
        ...prev,
        langContent: {
          ...prev.langContent,
          [prev.activeLanguage]: { ...content, body: { ...content.body, examples: next } }
        }
      }
    })
  }, [activeBodyText])

  // ─── Language management ──────────────────────────────────────────────────

  function addLanguage (code) {
    if (!code || form.langContent[code]) return
    setForm(prev => ({
      ...prev,
      activeLanguage: code,
      langContent: { ...prev.langContent, [code]: EMPTY_LANG_CONTENT() }
    }))
  }

  function switchLanguage (code) {
    setForm(prev => ({ ...prev, activeLanguage: code }))
  }

  function removeLanguage (code) {
    if (templateToEdit) return // can't remove language in edit mode
    setForm(prev => {
      const langContent = { ...prev.langContent }
      delete langContent[code]
      const remaining = Object.keys(langContent)
      return {
        ...prev,
        langContent,
        activeLanguage: code === prev.activeLanguage ? (remaining[0] ?? '') : prev.activeLanguage
      }
    })
  }

  // ─── Content field setter (scoped to active language) ─────────────────────

  function setContentField (path, value) {
    setForm(prev => {
      const content = { ...(prev.langContent[prev.activeLanguage] ?? EMPTY_LANG_CONTENT()) }
      const parts = path.split('.')
      let ref = content
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = { ...ref[parts[i]] }
        ref = ref[parts[i]]
      }
      ref[parts[parts.length - 1]] = value
      return { ...prev, langContent: { ...prev.langContent, [prev.activeLanguage]: content } }
    })
  }

  // ─── Header ───────────────────────────────────────────────────────────────

  function handleHeaderTypeChange (type) {
    setForm(prev => {
      const content = prev.langContent[prev.activeLanguage] ?? EMPTY_LANG_CONTENT()
      const updated = {
        ...content,
        header: type
          ? { type, content: content.header?.content ?? '', example: content.header?.example ?? '' }
          : null
      }
      return { ...prev, langContent: { ...prev.langContent, [prev.activeLanguage]: updated } }
    })
  }

  // ─── Buttons ──────────────────────────────────────────────────────────────

  function handleButtonConfigChange (val) {
    setForm(prev => {
      const content = prev.langContent[prev.activeLanguage] ?? EMPTY_LANG_CONTENT()
      let buttons = []
      if (val === 'QUICK_REPLY') {
        buttons = content.buttons.filter(b => b.type === 'QUICK_REPLY').length
          ? content.buttons
          : [{ type: 'QUICK_REPLY', text: '' }]
      } else if (val) {
        buttons = [{ type: val, text: '', url: '', phoneNumber: '' }]
      }
      return { ...prev, langContent: { ...prev.langContent, [prev.activeLanguage]: { ...content, buttons } } }
    })
  }

  function addQuickReply () {
    setForm(prev => {
      const content = prev.langContent[prev.activeLanguage] ?? EMPTY_LANG_CONTENT()
      return {
        ...prev,
        langContent: {
          ...prev.langContent,
          [prev.activeLanguage]: { ...content, buttons: [...content.buttons, { type: 'QUICK_REPLY', text: '' }] }
        }
      }
    })
  }

  function removeButton (i) {
    setForm(prev => {
      const content = prev.langContent[prev.activeLanguage] ?? EMPTY_LANG_CONTENT()
      return {
        ...prev,
        langContent: {
          ...prev.langContent,
          [prev.activeLanguage]: { ...content, buttons: content.buttons.filter((_, idx) => idx !== i) }
        }
      }
    })
  }

  function setButton (i, key, value) {
    setForm(prev => {
      const content = prev.langContent[prev.activeLanguage] ?? EMPTY_LANG_CONTENT()
      return {
        ...prev,
        langContent: {
          ...prev.langContent,
          [prev.activeLanguage]: {
            ...content,
            buttons: content.buttons.map((b, idx) => idx === i ? { ...b, [key]: value } : b)
          }
        }
      }
    })
  }

  // ─── Toolbar ──────────────────────────────────────────────────────────────

  function wrapSelection (open, close) {
    const el = textareaRef.current
    if (!el) return
    const start = el.selectionStart
    const end = el.selectionEnd
    const text = activeBodyText
    const newText = text.slice(0, start) + open + text.slice(start, end) + close + text.slice(end)
    setContentField('body.text', newText)
    setTimeout(() => { el.focus(); el.setSelectionRange(start + open.length, end + open.length) }, 0)
  }

  function insertVariable () {
    const el = textareaRef.current
    if (!el) return
    const count = extractVarCount(activeBodyText)
    const variable = `{{${count + 1}}}`
    const start = el.selectionStart
    const newText = activeBodyText.slice(0, start) + variable + activeBodyText.slice(start)
    setContentField('body.text', newText)
    setTimeout(() => { el.focus(); el.setSelectionRange(start + variable.length, start + variable.length) }, 0)
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  async function handleSubmit () {
    setLoading(true)
    try {
      if (templateToEdit) {
        // Edit: single language update
        const content = form.langContent[form.activeLanguage] ?? {}
        const payload = buildPayload(form.activeLanguage, content)
        await updateTemplate(client._id, templateToEdit._id, payload)
        toast.success('Template actualizado')
        onSaved()
      } else {
        // Create + auto-submit to Meta: one template per language
        const entries = Object.entries(form.langContent)
        const results = await Promise.allSettled(
          entries.map(async ([code, content]) => {
            const { data } = await createTemplate(client._id, buildPayload(code, content))
            await submitTemplate(client._id, data._id)
            return data
          })
        )
        const failed = results.filter(r => r.status === 'rejected')
        const ok = results.filter(r => r.status === 'fulfilled')
        if (ok.length > 0) toast.success(`${ok.length} template${ok.length > 1 ? 's enviados' : ' enviado'} a Meta para aprobación`)
        failed.forEach(r => {
          const msg = r.reason?.response?.data?.message
          if (Array.isArray(msg)) msg.forEach(m => toast.error(m))
          else toast.error(msg || 'Error al guardar template')
        })
        if (failed.length === 0) onSaved()
      }
    } catch (error) {
      const msg = error?.response?.data?.message
      if (Array.isArray(msg)) msg.forEach(m => toast.error(m))
      else toast.error(msg || 'Error al guardar el template')
    } finally {
      setLoading(false)
    }
  }

  function buildPayload (langCode, content) {
    const payload = {
      name: form.name,
      language: langCode,
      category: form.category,
      body: { text: content.body?.text, examples: content.body?.examples ?? [] }
    }
    if (content.header?.type) {
      payload.header = {
        type: content.header.type,
        content: content.header.content || undefined,
        example: content.header.example || undefined
      }
    }
    if (content.footer?.trim()) payload.footer = content.footer.trim()
    if (content.buttons?.length > 0) {
      payload.buttons = content.buttons.map(b => {
        const btn = { type: b.type, text: b.text }
        if (b.type === 'URL' && b.url) btn.url = b.url
        if (b.type === 'PHONE_NUMBER' && b.phoneNumber) btn.phoneNumber = b.phoneNumber
        return btn
      })
    }
    return payload
  }

  // ─── Derived values ───────────────────────────────────────────────────────

  const langCodes = Object.keys(form.langContent)
  const activeLang = form.langContent[form.activeLanguage] ?? EMPTY_LANG_CONTENT()
  const varCount = extractVarCount(activeBodyText)
  const previewBody = fillExamples(activeBodyText, activeLang.body.examples)
  const selectedLangLabel = LANGUAGES.find(l => l.value === form.activeLanguage)?.label ?? form.activeLanguage
  const availableLanguages = LANGUAGES.filter(l => !form.langContent[l.value])
  const btnConfigValue = activeLang.buttons.length === 0
    ? ''
    : activeLang.buttons[0].type === 'QUICK_REPLY' ? 'QUICK_REPLY' : activeLang.buttons[0].type

  const canSave = form.name && langCodes.length > 0 && langCodes.every(code => {
    const c = form.langContent[code]
    const count = extractVarCount(c.body.text)
    return c.body.text && c.body.examples.length === count
  })

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className='tf-bird-layout'>

      {/* ── Col 1: Languages ── */}
      <div className='tf-lang-col'>
        <p className='tf-col-label'>Idiomas</p>

        {/* Add language dropdown — only shows unselected options */}
        {availableLanguages.length > 0 && !templateToEdit && (
          <select
            className='tf-lang-select'
            value=''
            onChange={e => { addLanguage(e.target.value); e.target.value = '' }}
          >
            <option value=''>Agregar idioma</option>
            {availableLanguages.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        )}

        {/* Language list */}
        {langCodes.map(code => {
          const label = LANGUAGES.find(l => l.value === code)?.label ?? code
          const isActive = code === form.activeLanguage
          return (
            <div
              key={code}
              className={`tf-lang-item${isActive ? ' tf-lang-item--active' : ''}`}
              onClick={() => switchLanguage(code)}
            >
              <span className={`tf-lang-dot${isActive ? ' tf-lang-dot--active' : ''}`} />
              <span className='tf-lang-item-label'>{label}</span>
              {!templateToEdit && (
                <button
                  className='tf-lang-remove'
                  onClick={e => { e.stopPropagation(); removeLanguage(code) }}
                  title='Eliminar idioma'
                >×
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* ── Col 2: Form ── */}
      <div className='tf-form-col'>

        {/* Template name + Category */}
        <div className='tf-top-row'>
          <div className='tf-field'>
            <label>Nombre del template</label>
            <input
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
              placeholder='Nombre de tu template'
              disabled={!!templateToEdit}
            />
          </div>
          <div className='tf-field tf-field-sm'>
            <label>Categoría</label>
            <select value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}>
              <option value='MARKETING'>Marketing</option>
              <option value='UTILITY'>Utilidad</option>
              <option value='AUTHENTICATION'>Autenticación</option>
            </select>
          </div>
        </div>

        {langCodes.length === 0 && (
          <p className='tf-lang-hint'>Selecciona un idioma en el menú de la izquierda para continuar.</p>
        )}

        {form.activeLanguage && (
          <>
            <div className='tf-section-divider' />
            <p className='tf-content-title'>Contenido en {selectedLangLabel}</p>

            {/* Header type */}
            <div className='tf-field'>
              <label>Tipo de encabezado <span className='tf-optional'>Opcional</span></label>
              <select value={activeLang.header?.type ?? ''} onChange={e => handleHeaderTypeChange(e.target.value)}>
                {HEADER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {/* Header content */}
            {activeLang.header?.type && (
              <div className='tf-field tf-field-full' style={{ marginBottom: 14 }}>
                <label>{activeLang.header.type === 'TEXT' ? 'Texto del encabezado' : 'URL del archivo'}</label>
                <input
                  value={activeLang.header.content ?? ''}
                  onChange={e => setContentField('header.content', e.target.value)}
                  placeholder={activeLang.header.type === 'TEXT' ? 'ej. Clínica Ki' : 'https://...'}
                />
                {activeLang.header.type === 'TEXT' && (
                  <input
                    style={{ marginTop: 6 }}
                    value={activeLang.header.example ?? ''}
                    onChange={e => setContentField('header.example', e.target.value)}
                    placeholder='Valor de ejemplo (si el encabezado contiene una variable)'
                  />
                )}
              </div>
            )}

            {/* Message */}
            <div className='tf-field tf-field-full'>
              <label>Mensaje</label>
              <textarea
                ref={textareaRef}
                value={activeBodyText}
                onChange={e => setContentField('body.text', e.target.value)}
                placeholder='Escribe el mensaje aquí...'
                rows={5}
              />
              <div className='tf-toolbar'>
                <button type='button' title='Negrita' onClick={() => wrapSelection('*', '*')}><strong>B</strong></button>
                <button type='button' title='Cursiva' onClick={() => wrapSelection('_', '_')}><em>I</em></button>
                <button type='button' title='Tachado' onClick={() => wrapSelection('~', '~')}><s>S</s></button>
                <button type='button' title='Código' onClick={() => wrapSelection('```', '```')}>{'<>'}</button>
                <div className='tf-toolbar-sep' />
                <button type='button' className='tf-add-var-btn' onClick={insertVariable}>
                  + Agregar variable
                </button>
              </div>
            </div>

            {/* Variable examples */}
            {varCount > 0 && (
              <div className='tf-examples-block'>
                <p className='tf-examples-label'>Contenido de ejemplo <span className='tf-optional'>para {varCount > 1 ? 'las variables' : 'la variable'}</span></p>
                {Array.from({ length: varCount }).map((_, i) => (
                  <div key={i} className='tf-example-row'>
                    <span className='tf-var-tag'>{`{{${i + 1}}}`}</span>
                    <input
                      value={activeLang.body.examples[i] ?? ''}
                      onChange={e => {
                        const examples = [...activeLang.body.examples]
                        examples[i] = e.target.value
                        setContentField('body.examples', examples)
                      }}
                      placeholder={`Ejemplo para variable ${i + 1}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className='tf-field tf-field-full'>
              <div className='tf-label-row'>
                <label>Footer</label>
                <span className='tf-optional'>Opcional</span>
              </div>
              <input
                value={activeLang.footer ?? ''}
                onChange={e => setContentField('footer', e.target.value)}
                placeholder='Escribe el footer aquí...'
                maxLength={60}
              />
            </div>

            {/* Buttons type */}
            <div className='tf-field'>
              <label>Tipo de botones <span className='tf-optional'>Opcional</span></label>
              <select value={btnConfigValue} onChange={e => handleButtonConfigChange(e.target.value)}>
                {BUTTON_CONFIGS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            {/* Quick replies */}
            {btnConfigValue === 'QUICK_REPLY' && (
              <div className='tf-qr-list'>
                {activeLang.buttons.map((btn, i) => (
                  <div key={i} className='tf-qr-item'>
                    <input
                      value={btn.text}
                      onChange={e => setButton(i, 'text', e.target.value)}
                      placeholder={`Respuesta rápida ${i + 1}`}
                      maxLength={25}
                    />
                    <button className='tf-qr-remove' onClick={() => removeButton(i)}>×</button>
                  </div>
                ))}
                {activeLang.buttons.length < 3 && (
                  <button className='tf-add-qr' onClick={addQuickReply}>+ Agregar respuesta rápida</button>
                )}
              </div>
            )}

            {/* CTA button */}
            {(btnConfigValue === 'URL' || btnConfigValue === 'PHONE_NUMBER') && activeLang.buttons[0] && (
              <div className='tf-cta-block'>
                <div className='tf-field'>
                  <label>Texto del botón</label>
                  <input value={activeLang.buttons[0].text} onChange={e => setButton(0, 'text', e.target.value)} placeholder='ej. Visitar sitio web' maxLength={25} />
                </div>
                {btnConfigValue === 'URL' && (
                  <div className='tf-field'>
                    <label>URL</label>
                    <input value={activeLang.buttons[0].url ?? ''} onChange={e => setButton(0, 'url', e.target.value)} placeholder='https://...' />
                  </div>
                )}
                {btnConfigValue === 'PHONE_NUMBER' && (
                  <div className='tf-field'>
                    <label>Número de teléfono</label>
                    <input value={activeLang.buttons[0].phoneNumber ?? ''} onChange={e => setButton(0, 'phoneNumber', e.target.value)} placeholder='+573001234567' />
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className='tf-actions-row'>
              <button className='btn btn-tf-cancel' onClick={onCancel}>Cancelar</button>
              <button className='btn main-btn btn-tf-save' onClick={handleSubmit} disabled={!canSave || loading}>
                {loading ? 'Guardando...' : templateToEdit ? 'Actualizar template' : 'Enviar para aprobación'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Col 3: Preview ── */}
      <div className='tf-preview-col'>
        <p className='tf-preview-label'>Vista previa <span className='tf-optional'>(solo referencia)</span></p>

        <div className='wp-chat-window'>
          <div className='wp-chat-header'>
            <div className='wp-chat-avatar'>K</div>
            <div className='wp-chat-name'>Ki Planner</div>
          </div>
          <div className='wp-chat-body'>
            {form.activeLanguage && (
              <div className='wp-lang-badge'>{selectedLangLabel}</div>
            )}
            <div className='wp-msg-wrap'>
              <div className='wp-bubble'>
                {activeLang.header?.type === 'TEXT' && activeLang.header.content && (
                  <div className='wp-bubble-header'>{activeLang.header.content}</div>
                )}
                {activeLang.header?.type && activeLang.header.type !== 'TEXT' && (
                  <div className='wp-bubble-media'>[{activeLang.header.type}]</div>
                )}
                <div className='wp-bubble-body'>
                  {previewBody
                    ? <span style={{ whiteSpace: 'pre-wrap' }}>{previewBody}</span>
                    : <span className='wp-placeholder'>Texto del mensaje aquí...</span>}
                </div>
                {activeLang.footer?.trim() && (
                  <div className='wp-bubble-footer'>{activeLang.footer}</div>
                )}
                <div className='wp-bubble-time'>Ahora</div>
              </div>
              {activeLang.buttons?.length > 0 && (
                <div className='wp-btn-group'>
                  {activeLang.buttons.map((b, i) => (
                    <div key={i} className='wp-btn-preview'>
                      {b.type === 'URL' && '↗ '}
                      {b.type === 'PHONE_NUMBER' && '📞 '}
                      {b.text || `Botón ${i + 1}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className='wp-chat-input-bar'>
            <div className='wp-input-placeholder'>Escribe un mensaje</div>
            <div className='wp-send-btn'>➤</div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default TemplateForm
